import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable, { formatCount } from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ReportExportButtons from '../shared/ReportExportButtons';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};
const TODAY = new Date().toISOString().split('T')[0];

// Normalise a delivery-partner display name into the flat row key the
// backend uses for its per-partner count/sales buckets.
const partnerKey = (name) => name.toLowerCase().replace(/ /g, '_');

// Toggleable base columns (the dynamic per-partner columns are always shown
// and appended after these). Catalogue order = table order.
const BASE_COLUMNS = [
    { key: 'tenant_store_name', label: 'Tenant Name' },
    { key: 'tenant_group_name', label: 'Group Name' },
    { key: 'tenant_code', label: 'Tenant Code' },
    { key: 'tenant_unit_code', label: 'Unit Code' },
    { key: 'property_name', label: 'Property' },
    { key: 'subcategory_name', label: 'Subcategory' },
    { key: 'live_days', label: 'Live Days', align: 'right', isCount: true, format: formatCount, total: false },
    { key: 'dine_in_count', label: 'Dine In Count', align: 'right', isCount: true, format: formatCount, total: true },
    { key: 'dine_in_sales', label: 'Dine In Sales', isMoney: true, total: true },
    { key: 'total_delivery_partner_count', label: 'Delivery Count', align: 'right', isCount: true, format: formatCount, total: true },
    { key: 'total_delivery_partner_sales', label: 'Delivery Sales', isMoney: true, total: true },
];
const DEFAULT_COLUMNS = BASE_COLUMNS.map((c) => c.key);

const initialFilters = {
    startDate: yesterday(),
    endDate: yesterday(),
    property_id: '',
    subcategory_id: '',
    tenant_id: [],
};

const FbSalesReport = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions,
        visibleColumns, exportColumns, totalKeys,
    } = useColumnConfig(BASE_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        subcategoryId: filters.subcategory_id,
    });
    const { concessionaireId, propertyAccess } = lookups;

    // F&B sales summary is scoped to F&B subcategories / tenants only.
    const fbSubcategoryOptions = useMemo(
        () => (lookups.subcategories || [])
            .filter((s) => s.category_id?.category_name === 'F&B')
            .map((s) => ({ value: s._id, label: s.subcategory_name })),
        [lookups.subcategories],
    );
    const fbTenantOptions = useMemo(
        () => (lookups.tenants || [])
            .filter((tn) => tn.tenant_category_id?.category_name === 'F&B')
            .map((tn) => ({ value: tn._id, label: tn.tenant_store_name, sub: tn.tenant_code })),
        [lookups.tenants],
    );

    // Delivery partners drive the dynamic per-partner columns (Dine In excluded).
    useEffect(() => {
        if (!concessionaireId) return;
        let cancelled = false;
        axios
            .post(`${config.hostname}/concessionairedeliverypartner/getdeliverypartner`, { concessionaire_id: concessionaireId }, { headers })
            .then((res) => {
                if (cancelled) return;
                const names = (Array.isArray(res.data) ? res.data : [])
                    .map((item) => item.delivery_partner_id?.delivery_partner_name)
                    .filter((name) => name && name !== 'Dine In');
                setDeliveryPartners(names);
            })
            .catch((err) => console.warn('delivery partner lookup failed', err?.response?.status));
        return () => { cancelled = true; };
    }, [concessionaireId, headers]);

    const partnerColumns = useMemo(
        () => deliveryPartners.flatMap((p) => {
            const k = partnerKey(p);
            return [
                { key: `${k}_count`, label: t('{{partner}} Count', { partner: p }), align: 'right', isCount: true, format: formatCount, total: false },
                { key: `${k}_sales`, label: t('{{partner}} Sales', { partner: p }), isMoney: true, total: false },
            ];
        }),
        [deliveryPartners, t],
    );

    // Table + export use flat per-partner keys, so the dynamic columns can read
    // them directly without a render() accessor.
    const tableColumns = useMemo(() => [...visibleColumns, ...partnerColumns], [visibleColumns, partnerColumns]);
    const exportColumnsAll = useMemo(
        () => [
            ...exportColumns,
            ...partnerColumns.map((c) => ({ key: c.key, header: c.label, isMoney: c.isMoney === true, isCount: c.isCount === true, total: false })),
        ],
        [exportColumns, partnerColumns],
    );

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'property_id' || key === 'subcategory_id') next.tenant_id = [];
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    // Flatten the nested delivery_partner_counts / _sales buckets onto each row
    // so both the table and the exporter address them with flat keys.
    const flatten = (rows) => rows.map((item) => {
        const row = { ...item };
        deliveryPartners.forEach((p) => {
            const k = partnerKey(p);
            row[`${k}_count`] = item.delivery_partner_counts?.[`${k}_count`] ?? 0;
            row[`${k}_sales`] = Number(item.delivery_partner_sales?.[`${k}_sales`] ?? 0);
        });
        return row;
    });

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access_id: propertyAccess,
            start_date: filters.startDate,
            end_date: filters.endDate,
            property_id: filters.property_id,
            subcategory_id: filters.subcategory_id,
            tenant_id: filters.tenant_id,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getfbsalessummary`, payload, { headers });
            setReportData(flatten(Array.isArray(res.data) ? res.data : []));
            setHasRun(true);
            insertToLog(' generated F&B sales summary report');
        } catch (err) {
            console.error('F&B sales summary report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate F&B Sales Summary report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const totals = useMemo(() => {
        const acc = {};
        totalKeys.forEach((k) => {
            if (visibleColumns.some((c) => c.key === k)) {
                acc[k] = reportData.reduce((s, i) => s + (Number(i[k]) || 0), 0);
            }
        });
        return Object.keys(acc).length ? acc : null;
    }, [reportData, visibleColumns, totalKeys]);

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: TODAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: TODAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'subcategory_id', label: t('Sub Category'), type: 'select', options: fbSubcategoryOptions, placeholder: t('All sub categories') },
        { key: 'tenant_id', label: t('Tenants'), type: 'multiselect', options: fbTenantOptions, placeholder: t('All F&B tenants'), span: 'full' },
    ];

    const exportMetadata = useMemo(() => {
        const dates = filters.startDate === filters.endDate
            ? [[t('Date'), filters.startDate]]
            : [[t('From'), filters.startDate], [t('To'), filters.endDate]];
        const tenantLabel = filters.tenant_id.length
            ? filters.tenant_id.map((id) => nameOf(fbTenantOptions, id)).filter(Boolean).join(', ')
            : t('All');
        return [
            ...dates,
            [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
            [t('Subcategory'), nameOf(fbSubcategoryOptions, filters.subcategory_id) || t('All')],
            [t('Tenant'), tenantLabel],
            [t('Exported By'), login_name],
            [t('Exported On'), new Date().toLocaleString()],
        ];
    }, [filters, lookups, fbSubcategoryOptions, fbTenantOptions, login_name, t]);

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('F&B Sales Summary')}
                subtitle={t('Dine-in and delivery-partner sales by F&B tenant.')}
                actions={
                    <ReportExportButtons
                        heading={t('F&B Sales Report')}
                        metadata={exportMetadata}
                        columns={exportColumnsAll}
                        data={sortedData}
                        fileName="FBSalesSummary"
                        landscape
                        onAfterExport={(fmt) => insertToLog(` exported F&B sales summary report to ${fmt}`)}
                    />
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={BASE_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={tableColumns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    totals={totals}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No F&B sales found for the selected filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default FbSalesReport;
